/**
 * PlayTeaser.jsx
 * GRANULLO_PLAY_ARCADE_CABINET_V2
 *
 * Homepage Play section — styled as an arcade cabinet / game console screen.
 * Dark cabinet panel with neon borders, CRT scanlines, "PRESS START" CTA,
 * live leaderboard strip, and a scrolling scenario reel. Works in both
 * light and dark site themes (panel stays dark for max game-feel).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Shield, Trophy, AlertTriangle, Zap, Flame, Users, Gamepad2 } from 'lucide-react';

const SCENARIOS = [
  { id: 'ftx',      label: 'FTX — Commingling',         year: 2022, stages: 8, loss: '$8B',   diff: 'EXTREME',   color: '#F87171', hook: 'Can you prevent customer fund contamination before $8B vanishes?' },
  { id: 'enron',    label: 'ENRON — SPE Illusion',      year: 2001, stages: 7, loss: '$74B',  diff: 'LEGENDARY', color: '#FB923C', hook: 'Can you expose hidden debt before $74B evaporates?' },
  { id: 'wirecard', label: 'WIRECARD — Phantom Revenue', year: 2020, stages: 7, loss: '€1.9B', diff: 'HARD',      color: '#E879F9', hook: 'Can you detect forged proof before trust collapses?' },
  { id: 'svb',      label: 'SVB — Duration Mismatch',    year: 2023, stages: 6, loss: '$42B',  diff: 'HARD',      color: '#22D3EE', hook: 'Can you contain a $42B deposit run in 24 hours?' },
  { id: 'theranos', label: 'THERANOS — Phantom Tests',   year: 2015, stages: 6, loss: '$9B',   diff: 'HARD',      color: '#A78BFA', hook: 'Can you detect fabricated diagnostics before $9B vanishes?' },
];

const DIFF_COLOR = {
  LEGENDARY: '#F59E0B',
  EXTREME:   '#EF4444',
  HARD:      '#EC4899',
};

const LIVE_STATS = [
  { icon: Users, label: 'PLAYERS THIS WEEK', value: '3,247' },
  { icon: Trophy, label: 'HIGH SCORE',         value: '$74B SAVED' },
  { icon: Flame, label: 'STREAK RECORD',      value: '17 PREVENTED' },
  { icon: Zap,   label: 'WORLDS UNLOCKED',    value: '156' },
];

/* Live-feed ticker entries — mimic a multiplayer event stream */
const TICKER_EVENTS = [
  { who: 'JANE D.',  verb: 'prevented', amt: '$8B',   game: 'FTX 2022',      ago: '2m ago', color: '#F87171' },
  { who: 'ALEX R.',  verb: 'saved',      amt: '$74B',  game: 'Enron 2001',    ago: '7m ago', color: '#FB923C' },
  { who: 'KIRA M.',  verb: 'contained',  amt: '$42B',  game: 'SVB 2023',      ago: '11m ago', color: '#22D3EE' },
  { who: 'DEVON S.', verb: 'exposed',    amt: '€1.9B', game: 'Wirecard',      ago: '16m ago', color: '#E879F9' },
  { who: 'RUTH C.',  verb: 'blocked',    amt: '$9B',   game: 'Theranos 2015', ago: '22m ago', color: '#A78BFA' },
  { who: 'OMAR B.',  verb: 'prevented',  amt: '$4.5B', game: '1MDB 2016',     ago: '29m ago', color: '#10F58E' },
  { who: 'LENA T.',  verb: 'saved',      amt: '$450M', game: 'Mt. Gox 2014',  ago: '34m ago', color: '#F59E0B' },
  { who: 'JUN K.',   verb: 'contained',  amt: '$8B',   game: 'FTX 2022',      ago: '41m ago', color: '#F87171' },
  { who: 'MILA V.',  verb: 'exposed',    amt: '$74B',  game: 'Enron 2001',    ago: '48m ago', color: '#FB923C' },
  { who: 'RAFAEL P.',verb: 'prevented',  amt: '$1.6B', game: 'MF Global',     ago: '55m ago', color: '#22D3EE' },
];

export default function PlayTeaser() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const cycle = setInterval(() => setIdx(i => (i + 1) % SCENARIOS.length), 4200);
    return () => clearInterval(cycle);
  }, [paused]);

  const scenario = SCENARIOS[idx];
  const diffColor = DIFF_COLOR[scenario.diff] || '#EF4444';

  return (
    <section className="py-20 px-6 lg:px-8" data-testid="play-teaser-section">
      <div className="max-w-[1040px] mx-auto">

        {/* ═══ Eyebrow ribbon ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="flex items-center gap-3 mb-5"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm font-mono text-[10px] font-bold uppercase tracking-[0.22em]"
            style={{
              color: '#10F58E',
              background: 'rgba(16,245,142,0.08)',
              border: '1px solid rgba(16,245,142,0.30)',
              boxShadow: '0 0 24px rgba(16,245,142,0.18), inset 0 0 0 1px rgba(16,245,142,0.06)',
              letterSpacing: '0.22em',
            }}
          >
            <Gamepad2 size={12} strokeWidth={2.2} />
            Granullo Arcade
          </span>
          <motion.span
            className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase tracking-[0.2em]"
            style={{ color: '#F87171' }}
            animate={{ opacity: [1, 0.35, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#F87171', boxShadow: '0 0 8px #F87171' }} />
            Live
          </motion.span>
        </motion.div>

        {/* ═══ Title + intro ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          className="relative mb-4"
        >
          {/* Handwritten red invitation — positioned to feel like a margin scribble */}
          <motion.span
            initial={{ opacity: 0, rotate: -6, x: -6 }}
            whileInView={{ opacity: 1, rotate: -3, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
            className="inline-flex items-center gap-2 mb-3"
            style={{
              fontFamily: '"Kalam", cursive',
              fontSize: '22px',
              color: 'var(--rebel-red)',
              fontWeight: 700,
              lineHeight: 1,
              transformOrigin: 'left center',
            }}
            data-testid="play-handwritten-invite"
          >
            play the game
            <motion.span
              aria-hidden
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ fontSize: '24px', lineHeight: 1 }}
            >
              →
            </motion.span>
          </motion.span>
          <h2
            className="text-[36px] sm:text-[52px] leading-[0.98] tracking-[-0.03em] font-bold"
            style={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              color: 'var(--shell-text)',
            }}
            data-testid="play-teaser-heading"
          >
            Rewrite Financial History.
          </h2>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ delay: 0.12 }}
          className="text-[14px] sm:text-[15px] leading-[1.55] max-w-[600px] mb-10"
          style={{ color: 'var(--shell-text-muted, rgba(100,116,139,1))' }}
        >
          Step into the world's most infamous financial collapses. Make the decisions that could have
          saved them. Earn prestige titles, challenge rivals, and discover why Granullo exists.
        </motion.p>

        {/* ═══ ARCADE CABINET ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
          className="relative rounded-[14px] overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #0A0D14 0%, #05070C 100%)',
            border: '1px solid rgba(255,255,255,0.10)',
            boxShadow:
              '0 40px 120px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
          }}
          data-testid="play-teaser-preview"
        >
          {/* Scanline overlay */}
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none mix-blend-overlay"
            style={{
              backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 3px)',
              opacity: 0.6,
            }}
          />
          {/* Ambient corner glow */}
          <div
            aria-hidden
            className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${scenario.color}22 0%, transparent 60%)`, filter: 'blur(30px)' }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(16,245,142,0.12) 0%, transparent 60%)', filter: 'blur(30px)' }}
          />

          {/* ═══ LIVE-FEED TICKER — top of cabinet ═══ */}
          <style>{`
            @keyframes granulloArcadeMarquee {
              0%   { transform: translate3d(0, 0, 0); }
              100% { transform: translate3d(-50%, 0, 0); }
            }
            .gp-ticker-track {
              animation: granulloArcadeMarquee 55s linear infinite;
              will-change: transform;
            }
            .gp-ticker:hover .gp-ticker-track {
              animation-play-state: paused;
            }
          `}</style>
          <div
            className="relative gp-ticker overflow-hidden"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              background: 'linear-gradient(180deg, rgba(16,245,142,0.05), rgba(16,245,142,0) 80%)',
            }}
            data-testid="play-teaser-ticker"
          >
            {/* Edge fades */}
            <div aria-hidden className="absolute top-0 bottom-0 left-0 w-16 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to right, #05070C, transparent)' }} />
            <div aria-hidden className="absolute top-0 bottom-0 right-0 w-16 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to left, #05070C, transparent)' }} />
            {/* Live label */}
            <div className="absolute top-0 bottom-0 left-0 z-20 flex items-center gap-1.5 pl-4 pr-3"
              style={{
                background: 'linear-gradient(90deg, rgba(248,113,113,0.18), rgba(248,113,113,0) 100%)',
                borderRight: '1px solid rgba(248,113,113,0.25)',
              }}
            >
              <motion.span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: '#F87171', boxShadow: '0 0 8px #F87171' }}
                animate={{ opacity: [1, 0.2, 1] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span className="text-[9px] font-mono font-bold uppercase tracking-[0.22em]" style={{ color: '#F87171' }}>
                Live Feed
              </span>
            </div>
            {/* Marquee track */}
            <div className="gp-ticker-track flex whitespace-nowrap py-2.5 pl-[128px]">
              {[...TICKER_EVENTS, ...TICKER_EVENTS].map((ev, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 mx-5 text-[11px] font-mono uppercase tracking-[0.12em]"
                  style={{ color: 'rgba(255,255,255,0.72)' }}
                >
                  <span style={{ color: '#10F58E' }}>›</span>
                  <span style={{ color: '#fff', fontWeight: 700 }}>{ev.who}</span>
                  <span style={{ color: 'rgba(255,255,255,0.45)' }}>{ev.verb}</span>
                  <span style={{ color: ev.color, fontWeight: 700 }}>{ev.amt}</span>
                  <span style={{ color: 'rgba(255,255,255,0.35)' }}>on</span>
                  <span style={{ color: 'rgba(255,255,255,0.70)' }}>{ev.game}</span>
                  <span style={{ color: 'rgba(255,255,255,0.25)' }}>· {ev.ago}</span>
                </span>
              ))}
            </div>
          </div>

          {/* ═══ TOP BAR — live leaderboard strip ═══ */}
          <div className="relative flex flex-wrap items-center gap-4 sm:gap-6 px-5 sm:px-7 py-3.5"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            {LIVE_STATS.map((s, i) => {
              const Ic = s.icon;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Ic size={12} style={{ color: '#10F58E' }} />
                  <span className="text-[9px] font-mono uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.40)' }}>
                    {s.label}
                  </span>
                  <span className="text-[11px] font-mono font-bold tabular-nums" style={{ color: '#fff' }}>
                    {s.value}
                  </span>
                </div>
              );
            })}
          </div>

          {/* ═══ MAIN CARTRIDGE SCREEN ═══ */}
          <div className="relative px-5 sm:px-10 pt-10 pb-8">
            {/* Corner brackets */}
            <Bracket pos="tl" />
            <Bracket pos="tr" />
            <Bracket pos="bl" />
            <Bracket pos="br" />

            <AnimatePresence mode="wait">
              <motion.div
                key={scenario.id}
                initial={{ opacity: 0, y: 12, filter: 'blur(6px)' }}
                animate={{ opacity: 1, y: 0,  filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -10,   filter: 'blur(6px)' }}
                transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                className="relative"
                data-testid="play-teaser-card"
              >
                {/* Meta row */}
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-[10px] font-mono font-bold px-2 py-1 rounded-sm"
                    style={{
                      color: scenario.color,
                      background: `${scenario.color}14`,
                      border: `1px solid ${scenario.color}55`,
                      boxShadow: `0 0 16px ${scenario.color}22`,
                    }}
                  >
                    LEVEL {String(idx + 1).padStart(2, '0')} / {String(SCENARIOS.length).padStart(2, '0')}
                  </span>
                  <span className="text-[10px] font-mono font-bold tabular-nums px-2 py-1 rounded-sm"
                    style={{
                      color: '#F87171',
                      background: 'rgba(248,113,113,0.08)',
                      border: '1px solid rgba(248,113,113,0.35)',
                    }}
                  >
                    HISTORICAL COLLAPSE · {scenario.year}
                  </span>
                  <span className="text-[10px] font-mono font-bold uppercase tracking-[0.16em] px-2 py-1 rounded-sm"
                    style={{
                      color: diffColor,
                      background: `${diffColor}14`,
                      border: `1px solid ${diffColor}55`,
                    }}
                  >
                    ▲ {scenario.diff}
                  </span>
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.42)' }}>
                    {scenario.stages} STAGES
                  </span>
                  <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.42)' }}>
                    LOSS AT STAKE <span style={{ color: '#fff' }}>{scenario.loss}</span>
                  </span>
                </div>

                {/* Big title */}
                <h3
                  className="text-[30px] sm:text-[44px] leading-[1.02] tracking-tight font-extrabold mb-3 font-mono"
                  style={{
                    color: '#fff',
                    textShadow: `0 0 22px ${scenario.color}55, 0 2px 0 rgba(0,0,0,0.6)`,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {scenario.label}
                </h3>

                {/* Hook with CRT cursor */}
                <p className="text-[14px] sm:text-[16px] leading-[1.55] max-w-[680px] mb-7 flex items-start gap-2"
                  style={{ color: scenario.color, fontFamily: '"IBM Plex Mono", ui-monospace, monospace' }}
                >
                  <span className="font-bold" style={{ color: '#10F58E' }}>&gt;</span>
                  <span>
                    {scenario.hook}
                    <motion.span
                      className="inline-block ml-1 align-[-1px]"
                      style={{
                        display: 'inline-block',
                        width: 8,
                        height: 14,
                        background: '#10F58E',
                        verticalAlign: 'text-bottom',
                      }}
                      animate={{ opacity: [1, 0, 1] }}
                      transition={{ duration: 1.0, repeat: Infinity, ease: 'linear' }}
                    />
                  </span>
                </p>

                {/* Reward chips */}
                <div className="flex flex-wrap items-center gap-2 mb-8">
                  <RewardChip icon={<AlertTriangle size={11} />} color="#F87171" label="+ drift XP" />
                  <RewardChip icon={<Shield size={11} />}        color="#10F58E" label="+ control badge" />
                  <RewardChip icon={<Trophy size={11} />}        color="#F5A623" label="+ prestige title" />
                  <RewardChip icon={<Flame size={11} />}         color="#E879F9" label="+ streak combo" />
                </div>

                {/* Progress dots — mini level selector */}
                <div className="flex items-center gap-2">
                  {SCENARIOS.map((s, i) => (
                    <button
                      key={s.id}
                      onClick={() => setIdx(i)}
                      className="relative rounded-full transition-all cursor-pointer"
                      style={{
                        width: i === idx ? 28 : 10,
                        height: 10,
                        background: i === idx ? s.color : 'rgba(255,255,255,0.14)',
                        border: 'none',
                        boxShadow: i === idx ? `0 0 14px ${s.color}80` : 'none',
                      }}
                      data-testid={`play-scenario-dot-${s.id}`}
                      aria-label={`Select ${s.label}`}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ═══ BOTTOM PANEL — PRESS START ═══ */}
          <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-4 px-5 sm:px-7 py-5"
            style={{
              borderTop: '1px solid rgba(255,255,255,0.08)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(16,245,142,0.03))',
            }}
          >
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="text-[9px] font-mono uppercase tracking-[0.24em]" style={{ color: 'rgba(255,255,255,0.38)' }}>
                Insert coin
              </span>
              <span className="text-[12px] font-mono" style={{ color: 'rgba(255,255,255,0.70)' }}>
                Free to play · No signup to try the first world
              </span>
            </div>
            <motion.button
              onClick={() => navigate('/play')}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="relative flex items-center justify-center gap-3 px-7 py-4 rounded-sm font-mono font-bold uppercase text-[13px] tracking-[0.18em] cursor-pointer"
              style={{
                background: 'linear-gradient(180deg, #1AFE9A, #0CCB74)',
                color: '#05140D',
                border: '1px solid rgba(0,0,0,0.12)',
                boxShadow:
                  '0 0 28px rgba(16,245,142,0.45), 0 6px 0 rgba(10,100,60,0.6), inset 0 1px 0 rgba(255,255,255,0.40)',
              }}
              data-testid="play-teaser-cta"
            >
              <Play size={16} strokeWidth={2.6} fill="#05140D" />
              Press Start
              <motion.span
                className="absolute inset-0 rounded-sm pointer-events-none"
                style={{ border: '1px solid rgba(16,245,142,0.6)' }}
                animate={{ opacity: [0, 0.8, 0], scale: [1, 1.06, 1.06] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
              />
            </motion.button>
          </div>
        </motion.div>

        {/* ═══ FOOTNOTE — up next reel ═══ */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[10px] font-mono uppercase tracking-[0.2em]"
          style={{ color: 'var(--shell-text-muted, rgba(100,116,139,1))' }}
        >
          <span>Up next</span>
          {SCENARIOS.map((s, i) => (
            <React.Fragment key={s.id}>
              <span
                className="inline-flex items-center gap-1.5 cursor-pointer hover:opacity-100 transition-opacity"
                style={{ opacity: i === idx ? 1 : 0.55 }}
                onClick={() => setIdx(i)}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />
                {s.label.split('—')[0].trim()}
              </span>
              {i < SCENARIOS.length - 1 && <span style={{ opacity: 0.3 }}>/</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Subcomponents ─── */
function Bracket({ pos }) {
  const common = {
    position: 'absolute',
    width: 14,
    height: 14,
    borderColor: 'rgba(16,245,142,0.45)',
    pointerEvents: 'none',
  };
  const map = {
    tl: { top: 12,    left: 12,    borderTop: '1px solid',  borderLeft: '1px solid' },
    tr: { top: 12,    right: 12,   borderTop: '1px solid',  borderRight: '1px solid' },
    bl: { bottom: 12, left: 12,    borderBottom: '1px solid', borderLeft: '1px solid' },
    br: { bottom: 12, right: 12,   borderBottom: '1px solid', borderRight: '1px solid' },
  };
  return <span style={{ ...common, ...map[pos] }} />;
}

function RewardChip({ icon, color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-mono font-semibold uppercase tracking-[0.14em]"
      style={{
        color,
        background: `${color}12`,
        border: `1px solid ${color}40`,
      }}
    >
      {icon}
      {label}
    </span>
  );
}
